import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { soundManager } from '../systems/SoundManager';

export class StyledButton extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Rectangle;
  private label!: Phaser.GameObjects.Text;
  private shadow!: Phaser.GameObjects.Rectangle;
  private shine!: Phaser.GameObjects.Rectangle;
  private isPressed = false;
  private baseColor: number;
  private hoverColor: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    private options: {
      width?: number;
      height?: number;
      bgColor?: number;
      shadowColor?: number;
      textColor?: string;
      fontSize?: string;
      radius?: number;
      onClick?: () => void;
      sound?: boolean;
    } = {}
  ) {
    super(scene, x, y);

    const width = options.width ?? 220;
    const height = options.height ?? 60;
    const bgColor = options.bgColor ?? parseInt(DESIGN.colors.primary.replace('#', '0x'), 16);
    const shadowColor = options.shadowColor ?? parseInt(DESIGN.colors.primaryDark.replace('#', '0x'), 16);
    const textColor = options.textColor ?? '#ffffff';
    const fontSize = options.fontSize ?? '24px';

    this.baseColor = bgColor;
    this.hoverColor = this.lighten(bgColor, 20);

    this.shadow = scene.add.rectangle(0, 8, width, height, shadowColor, 1);
    this.shadow.setOrigin(0.5);
    this.add(this.shadow);

    this.bg = scene.add.rectangle(0, 0, width, height, bgColor, 1);
    this.bg.setOrigin(0.5);
    this.bg.setStrokeStyle(0);
    this.bg.setInteractive({ useHandCursor: true });
    this.add(this.bg);

    // Glossy shine overlay (non-interactive, visual only)
    this.shine = scene.add.rectangle(-width / 2 + 4, -height / 2 + 3, width - 8, height / 2 - 4, 0xffffff, 0.25);
    this.shine.setOrigin(0, 0);
    this.add(this.shine);

    this.label = scene.add.text(0, 0, text, {
      fontFamily: DESIGN.fonts.body,
      fontSize,
      color: textColor,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add(this.label);

    this.bindEvents();
    scene.add.existing(this);
  }

  private bindEvents(): void {
    this.bg.on('pointerover', () => {
      if (!this.scene || !this.scene.tweens) return;
      this.bg.setFillStyle(this.hoverColor);
      this.scene.tweens.add({
        targets: this,
        scale: 1.05,
        duration: 150,
        ease: 'Back.easeOut',
      });
    });

    this.bg.on('pointerout', () => {
      if (!this.scene || !this.scene.tweens) return;
      if (this.isPressed) {
        this.y += 6;
        this.isPressed = false;
      }
      this.bg.setFillStyle(this.baseColor);
      this.scene.tweens.add({
        targets: this,
        scale: 1,
        duration: 150,
        ease: 'Back.easeOut',
      });
    });

    this.bg.on('pointerdown', () => {
      if (!this.scene || !this.scene.tweens) return;
      this.isPressed = true;
      this.scene.tweens.add({
        targets: this,
        y: this.y + 6,
        scale: 0.95,
        duration: 80,
        ease: 'Quad.easeInOut',
      });
    });

    this.bg.on('pointerup', () => {
      if (!this.isPressed) return;
      this.isPressed = false;
      if (this.options.sound !== false) soundManager.play('click');
      // Execute callback immediately; the scene may restart/transition and destroy this button.
      this.options.onClick?.();

      if (!this.scene || !this.scene.tweens) return;
      this.scene.tweens.add({
        targets: this,
        y: this.y - 6,
        scale: 1.05,
        duration: 200,
        ease: 'Back.easeOut',
        onComplete: () => {
          if (!this.scene || !this.scene.tweens) return;
          this.scene.tweens.add({
            targets: this,
            scale: 1,
            duration: 150,
            ease: 'Sine.easeInOut',
          });
        },
      });
    });
  }

  private lighten(color: number, percent: number): number {
    const r = Math.min(255, ((color >> 16) & 0xff) + percent);
    const g = Math.min(255, ((color >> 8) & 0xff) + percent);
    const b = Math.min(255, (color & 0xff) + percent);
    return (r << 16) | (g << 8) | b;
  }

  setText(text: string): void {
    this.label.setText(text);
  }

  setVisible(visible: boolean): this {
    super.setVisible(visible);
    if (visible) {
      this.bg.setInteractive({ useHandCursor: true });
    } else {
      this.bg.disableInteractive();
    }
    return this;
  }
}
